/* ClientDealsTab — per-client kanban-lite list of ongoing deals.
 *
 * Renders inside the client folder (5-tab shell). Same `deal` records as
 * /obchody just filtered by contact_id (§7.7, §7.8 — one record, two views).
 *
 * REALITNÍ OBCHOD SE KRESLÍ JINAK. Od 9/2026 sem patří i prodej a koupě
 * nemovitosti. Ty se NEVYKRESLUJÍ jako řádek s poskytovatelem a body — u bytu
 * nedává ani jedno smysl. Místo řádku se nahoře objeví karta (RealityVeSlozce)
 * s tím, na co se u nemovitosti opravdu ptáme: kolik lidí bylo na prohlídce,
 * odkaz, kterým se majitel podívá sám, stav rezervace a hypotéka.
 *
 * FINANČNÍ OBCHODY SE TÍM NEMĚNÍ. Realitní se ze seznamu vyjmou a zbytek
 * prochází přesně toutéž cestou jako dřív — stejné skupiny, stejný řádek,
 * stejné počty ve statistikách. Realitní obchod se do BJ nepočítá, protože
 * provize z nemovitosti se v bodech neměří.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronRight, Briefcase, Clock, AlertTriangle, Check, X, Minus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PRODUCT_META, productMeta, fmtCZ } from "@/lib/dealMeta";
import NewDealModal from "@/components/crm/NewDealModal";
import RealityVeSlozce from "@/components/crm/RealityVeSlozce";
import { jeRealitni, nactiRealityKlienta } from "@/lib/reality/klientApi";
import { useLanguage } from "@/contexts/LanguageContext";

// Robustnost: API pole může přijít jako ne-pole ({}, null, string) nebo s null-položkami.
// Vždy vrať čisté pole objektů, ať .filter/.reduce/.map nespadne (bílá stránka).
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

const STAGE_GROUPS = [
  /* Fáze obchodu = tokeny --faze-* (CRM-08), stejné jako sloupce Pipeline (lib/dealMeta). */
  { id: "analyza", title: "Fin. analýza / Nabídnuto", titleSk: "Fin. analýza / Ponúknuté", color: "var(--faze-analyza)" },
  { id: "proces",  title: "Žádost / V procesu",        titleSk: "Žiadosť / V procese",     color: "var(--faze-proces)" },
  { id: "podpis",  title: "Schválení / Podpis",        titleSk: "Schválenie / Podpis",     color: "var(--faze-podpis)" },
  { id: "cerpani", title: "Čerpání / Čekám na provize", titleSk: "Čerpanie / Čakám na províziu", color: "var(--faze-cerpani)" },
];

export default function ClientDealsTab({ contactId, contactName }) {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [tab, setTab] = useState("open"); // open | won | archived
  /* null = ještě nenačteno / klient nemá nic realitního. Chyba realitního
     přehledu NESMÍ shodit seznam obchodů — to je samostatná sekce navíc. */
  const [reality, setReality] = useState(null);
  const [realityChyba, setRealityChyba] = useState(false);
  const [predvolba, setPredvolba] = useState(null); // předvyplněný typ v modalu

  const nactiReality = useCallback(async () => {
    try {
      setReality(await nactiRealityKlienta(contactId, lang));
      setRealityChyba(false);
    } catch {
      /* „Nic realitního" a „nepodařilo se to načíst" jsou dvě různé věty.
         Kdyby se chyba tvářila jako prázdno, makléř by ve složce prodávajícího
         neviděl jeho zakázku a založil by ji podruhé. */
      setReality(null);
      setRealityChyba(true);
    }
  }, [contactId, lang]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/api/deals", { params: { contact_id: contactId } });
      setDeals(asArr(data));
    } catch {
      // CHYBA API != prázdno — nastav error stav (viditelná chyba + retry), ne falešné „žádné obchody".
      setError(true);
      setDeals([]);
      toast.error(t("auto.clientDealsTab.nelzeNacistObchodyKlienta", 'Nelze načíst obchody klienta'));
    } finally {
      setLoading(false);
    }
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { nactiReality(); }, [nactiReality]);

  // QA-B3 — when opening a deal from inside the client folder slide-over,
  // we must dismiss the overlay so the user actually sees /obchody/{id}.
  const openPath = useCallback((path) => {
    window.dispatchEvent(new CustomEvent("close-all-client-folders"));
    navigate(path);
  }, [navigate]);

  const openDeal = useCallback((dealId) => openPath(`/obchody/${dealId}`), [openPath]);

  /* Realitní obchody se ze seznamu vyjmou — kreslí je karta nad ním. Kdyby
     zůstaly, viděl by makléř tutéž nemovitost dvakrát a u jedné z kopií
     „0 BJ", což u prodeje bytu není nula, ale nesmysl. */
  const financni = deals.filter((d) => !jeRealitni(d.product_type));
  const open = financni.filter((d) => d.result === "open");
  const won = financni.filter((d) => d.result === "won");
  const archived = financni.filter((d) => d.result === "lost" || d.result === "cancelled");

  const totalBj = open.reduce((s, d) => s + (d.value_bj || 0), 0);
  const wonBj = won.reduce((s, d) => s + (d.value_bj || 0), 0);

  const list = tab === "open" ? open : tab === "won" ? won : archived;

  const grouped = STAGE_GROUPS.map((g) => ({
    ...g,
    items: list.filter((d) => (d.board_stage || "analyza") === g.id),
  }));

  const maReality = Boolean(reality)
    && ((reality.prodej?.length || 0) + (reality.koupe?.length || 0) + (reality.predavky?.length || 0)) > 0;

  return (
    <div className="space-y-4" data-testid="client-deals-tab">
      {/* Realitní obchod — jiný obsah než finanční, proto vlastní karta.
          Stojí NAD seznamem: u makléře je nemovitost hlavní věc složky. */}
      <RealityVeSlozce
        prehled={reality}
        onOtevrit={openPath}
        onNovaHypoteka={() => { setPredvolba("hypoteka"); setNewOpen(true); }}
      />

      {realityChyba && (
        <div
          data-testid="client-reality-error"
          className="rounded-xl px-3 py-2 text-drobny"
          style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--muted)" }}
        >
          {t("realSlozka.chyba", 'Realitní část se nepodařilo načíst. Obchody níž jsou v pořádku.')}
        </div>
      )}

      {/* Stat strip */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label={t("auto.clientDealsTab.rozjednaneObchody", 'Rozjednané obchody')} value={open.length} sub={`${fmtCZ(totalBj)} BJ`} color="var(--accent)" />
        <Stat label={t("auto.clientDealsTab.vyhraneSmlouvy", 'Vyhrané → smlouvy')} value={won.length} sub={`${fmtCZ(wonBj)} BJ`} color={won.length > 0 ? "var(--positive-text)" : "var(--muted)"} />
        <Stat label={t("auto.clientDealsTab.archivovane", 'Archivované')} value={archived.length} sub={t("auto.clientDealsTab.prohraZruseno", 'Prohra/Zrušeno')} color="var(--muted)" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-1.5" data-testid="client-deals-tabs">
          <TabBtn id="open" active={tab} onClick={setTab}>{t("auto.clientDealsTab.rozjednane", 'Rozjednané')} ({open.length})</TabBtn>
          <TabBtn id="won" active={tab} onClick={setTab}>{t("auto.clientDealsTab.vyhrane", 'Vyhrané')} ({won.length})</TabBtn>
          <TabBtn id="archived" active={tab} onClick={setTab}>{t("auto.clientDealsTab.archiv", 'Archiv')} ({archived.length})</TabBtn>
        </div>
        <button
          onClick={() => setNewOpen(true)}
          data-testid="client-deals-new-btn"
          className="inline-flex h-11 md:h-9 items-center gap-1.5 px-3 rounded-[var(--radius-sm)] text-sm font-semibold"
          style={{ background: "var(--accent)", color: "var(--on-accent)" }}
        >
          <Plus size={14} /> {t("obchody.newDeal", 'Nový obchod')}
        </button>
      </div>

      {loading && <div className="py-8 text-center text-sm" style={{ color: "var(--muted)" }}>{t("auto.clientDealsTab.nacitam", 'Načítám…')}</div>}

      {!loading && error && (
        <div className="rounded-[var(--radius)] p-8 text-center" data-testid="client-deals-error" style={{ background: "var(--surface)", border: "1px solid color-mix(in srgb, var(--negative) 30%, var(--line))" }}>
          <AlertTriangle size={20} className="mx-auto mb-2" style={{ color: "var(--negative-text)" }} />
          <div className="text-[13px] mb-1" style={{ color: "var(--ink)" }}>{t("auto.clientDealsTab.obchodySeNepodariloNacist", 'Obchody se nepodařilo načíst')}</div>
          <div className="text-drobny mb-3" style={{ color: "var(--muted)" }}>{t("auto.clientDealsTab.zkusToZnovuNejde", 'Zkus to znovu — nejde o prázdný seznam, ale o chybu spojení.')}</div>
          <button
            onClick={load}
            data-testid="client-deals-retry"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold"
            style={{ background: "var(--accent)", color: "var(--on-accent)" }}
          >
            {t("auto.spolecne.zkusitZnovu", 'Zkusit znovu')}
          </button>
        </div>
      )}

      {!loading && !error && list.length === 0 && !maReality && (
        <div className="rounded-2xl p-8 text-center" style={{ background: "var(--surface)", border: "1px dashed var(--line)" }}>
          <Briefcase size={20} className="mx-auto mb-2" style={{ color: "var(--muted)" }} />
          <div className="text-[13px] mb-1" style={{ color: "var(--ink)" }}>{t("auto.clientDealsTab.zadneObchodyVTeto", 'Žádné obchody v této kategorii')}</div>
          <div className="text-drobny" style={{ color: "var(--muted)" }}>{t("auto.clientDealsTab.klikniNovyObchodPro", 'Klikni „Nový obchod“ pro založení.')}</div>
        </div>
      )}

      {!loading && !error && list.length > 0 && tab === "open" && (
        <div className="space-y-4">
          {grouped.map((g) => g.items.length > 0 && (
            <div key={g.id} data-testid={`client-deals-group-${g.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: g.color }} />
                <span className="text-popisek uppercase tracking-[0.18em] font-semibold" style={{ color: g.color }}>{lang === 'sk' ? g.titleSk : g.title}</span>
                <span className="text-drobny" style={{ color: "var(--muted)" }}>· {g.items.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {g.items.map((d) => <DealRow key={d.id} deal={d} onOpen={() => openDeal(d.id)} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && list.length > 0 && tab !== "open" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {list.map((d) => <DealRow key={d.id} deal={d} onOpen={() => openDeal(d.id)} />)}
        </div>
      )}

      <NewDealModal
        open={newOpen}
        onClose={() => { setNewOpen(false); setPredvolba(null); }}
        contactId={contactId}
        contactName={contactName}
        /* Realitní typy nabízíme JEN ve složce klienta. Na /obchody a
           v hypoteční pipeline zůstává výběr přesně takový, jaký byl. */
        povolitReality
        predvolenyTyp={predvolba}
        onCreated={(vysledek, meta) => {
          setNewOpen(false);
          setPredvolba(null);
          load();
          nactiReality();
          /* Zakázka ani poptávka nejsou „obchod" v tom smyslu, jaký umí
             ukázat /obchody/:id. Prodej má kartu nemovitosti (adresa,
             prohlídky, odkaz pro majitele), nákup kartu kupujícího (co hledá,
             co jsme mu nabídli). Bez tohohle by makléř skončil na obrazovce,
             kde ani jedno z toho není. */
          openPath(
            /* Obojí vede na KARTU OBCHODU: realitní zakázka i poptávka jsou
               obchodní případ. U poptávky je `id` rovnou id obchodu, u zakázky
               přišel `deal_id` ze serveru; bez něj se jde přes `/zakazky/:id`,
               což přesměruje. */
            meta?.druh === "zakazka"
              ? (vysledek.deal_id ? `/obchody/${vysledek.deal_id}` : `/zakazky/${vysledek.id}`)
              : meta?.druh === "poptavka" ? `/obchody/${vysledek.id}`
                : `/obchody/${vysledek.id}`,
          );
        }}
      />
    </div>
  );
}

function TabBtn({ id, active, onClick, children }) {
  const on = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`client-deals-tab-${id}`}
      aria-pressed={on}
      /* Stejný vzhled jako FiltrCipy (aktivní = --accent-soft + --accent); vlastní
         tlačítko zůstává kvůli testid `client-deals-tab-*` a počtu „(n)“. */
      className={`h-11 md:h-9 px-3 rounded-full border text-sm font-medium transition-colors duration-150 ${on
        ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]"
        : "border-[var(--line)] bg-[var(--surface)] text-[var(--ink)] hover:bg-[var(--accent-soft)]"}`}
    >
      {children}
    </button>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className="rounded-xl p-3" style={{ background: "var(--surface)", border: "1px solid var(--line)" }}>
      <div className="text-popisek uppercase tracking-wider" style={{ color: "var(--muted)" }}>{label}</div>
      <div className="text-2xl tabular-nums mt-0.5" style={{ fontFamily: "var(--font-display)", fontWeight: 600, color }}>{value}</div>
      <div className="text-drobny mt-0.5" style={{ color: "var(--muted)" }}>{sub}</div>
    </div>
  );
}

function DealRow({ deal, onOpen }) {
  const { t } = useLanguage();
  const meta = productMeta(deal.product_type);
  const stale = !(Array.isArray(deal.activity) && deal.activity.length > 0) && deal.result === "open";
  const resultIcon = deal.result === "won" ? Check : deal.result === "lost" ? X : deal.result === "cancelled" ? Minus : null;
  return (
    <button
      onClick={onOpen}
      data-testid={`client-deal-row-${deal.id}`}
      className="text-left rounded-[var(--radius-sm)] px-3 py-2.5 transition-colors hover:bg-[var(--accent-soft)]"
      style={{ background: "var(--surface)", border: "1px solid var(--line)", borderLeft: `3px solid ${meta.color}` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-maly font-semibold leading-tight line-clamp-2" style={{ color: "var(--ink)" }}>
          {deal.title}
        </div>
        <span
          className="text-popisek font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider flex-shrink-0"
          style={{ color: `color-mix(in srgb, ${meta.color} 80%, var(--ink))`, background: `color-mix(in srgb, ${meta.color} 15%, var(--surface))` }}
        >
          {meta.label}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between text-drobny">
        <span className="inline-flex items-center gap-1" style={{ color: stale ? "var(--negative-text)" : "var(--muted)" }}>
          {resultIcon ? <span className="inline-flex items-center gap-1">{(() => { const I = resultIcon; return <I size={11} />; })()} {deal.result}</span> :
           stale ? <><AlertTriangle size={11} /> {t("obchody.stale", 'bez aktivity')}</> :
           <><Clock size={11} /> {deal.stage_label || (t("auto.clientDealsTab.vProcesu", 'v procesu'))}</>}
        </span>
        <span className="tabular-nums font-semibold" style={{ color: "var(--accent)" }}>
          {deal.value_bj ? `${fmtCZ(deal.value_bj)} BJ` : "—"}
        </span>
      </div>
      <div className="mt-1 text-drobny flex items-center gap-1" style={{ color: "var(--muted)" }}>
        {t("auto.clientDealsTab.otevritDetail", 'Otevřít detail')} <ChevronRight size={11} />
      </div>
    </button>
  );
}
